<template>
  <div class="bg-grey-lighten-5 min-h-screen">
    <!-- Barra de Herramientas Superior -->
    <v-card rounded="0" elevation="0" class="bg-white toolbar-card">
      <v-container fluid class="d-flex align-center justify-left toolbar-container">
        <div class="d-flex align-center toolbar-actions">
          <!-- Botón Responder -->
          <v-btn v-if="ticket" :disabled="isDisabled(ticket.estado)" @click="scrollToAnswer()" 
            variant="text" color="grey-darken-3" class="toolbar-btn" prepend-icon="">
            <template v-slot:prepend>
              <v-icon class="toolbar-icon" style="color: #424242;">fa:fa-solid fa-reply</v-icon>
            </template>
            <span class="btn-text">Responder</span>
          </v-btn>

          <v-divider vertical class="toolbar-divider"></v-divider>

          <!-- Botón Cerrar Ticket -->
          <v-btn v-if="cierreBTN && !isRoleVisitor()" @click="closedTicket(ticket.idticket)" 
            variant="text" color="grey-darken-3" class="toolbar-btn" prepend-icon="">
            <template v-slot:prepend>
              <v-icon class="toolbar-icon" style="color: #2E7D32;">fa:fa-regular fa-circle-check</v-icon>
            </template>
            <span class="btn-text">Cerrar</span>
          </v-btn>

          <!-- Botón Eliminar -->
          <v-btn v-if="ticket" @click="deleteTicket(ticket.idticket)" 
            variant="text" color="grey-darken-3" class="toolbar-btn" prepend-icon="">
            <template v-slot:prepend>
              <v-icon class="toolbar-icon" style="color: #C62828;">fa:fa-solid fa-trash-can</v-icon>
            </template>
            <span class="btn-text">Eliminar</span>
          </v-btn>
        </div>
      </v-container>
    </v-card>

    <v-container fluid class="px-4 px-md-8 py-6 pb-16" style="max-width: 1600px;">
      
      <!--- SKELETON LOADERS --->
      <v-row v-if="ticket == null">
        <v-col cols="12" md="8" lg="9">
          <v-skeleton-loader type="article, paragraph@3, actions" class="bg-transparent"></v-skeleton-loader>
        </v-col>
        <v-col cols="12" md="4" lg="3">
          <v-skeleton-loader type="list-item-avatar-two-line, list-item-two-line@3" class="bg-transparent"></v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row v-if="ticket">
        
        <!-- COLUMNA PRINCIPAL: CONTENIDO Y CONVERSACIÓN -->
        <v-col cols="12" md="8" lg="9" class="pr-md-6">
          
          <!-- HILOS ANTERIORES (Tickets Anidados) -->
          <div v-if="ticket.tickets_anidados && ticket.tickets_anidados.length > 0" class="mb-8">
            <div class="d-flex align-center justify-center mb-6">
              <v-divider></v-divider>
              <span class="text-caption text-medium-emphasis px-4 text-uppercase font-weight-bold">Historial Previo</span>
              <v-divider></v-divider>
            </div>

            <div v-for="(hilo, i) in ticket.tickets_anidados" :key="i" class="mb-4">
              <v-expansion-panels variant="accordion" elevation="0" class="border rounded-lg bg-white">
                <v-expansion-panel>
                  <v-expansion-panel-title class="py-2">
                    <div class="d-flex align-center w-100">
                      <div class="mr-4 text-medium-emphasis font-weight-bold text-caption">
                        {{ fechasmall("DD/MM/YY", hilo.created_at) }}
                      </div>
                      <div class="text-subtitle-2 text-truncate flex-grow-1 font-weight-bold text-grey-darken-3">
                        {{ i == 0 ? hilo.asunto : 'Referencia #' + i }}
                      </div>
                      <v-chip size="x-small" variant="tonal" class="ml-2" color="grey">
                        {{ hilo.categoria.nombre }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <div class="pt-4 px-2 caja-texto-styles" v-html="hilo.body"></div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            
            <div class="d-flex align-center justify-center my-6">
              <v-icon size="small" color="grey-lighten-1" class="mr-2">mdi-arrow-down</v-icon>
              <span class="text-caption text-grey-darken-1 font-weight-medium">Ticket Actual</span>
            </div>
          </div>

          <!-- TICKET ACTUAL (Hero) -->
          <div class="bg-white rounded-lg border ticket-header-card mb-6 shadow-sm">
            <!-- Header del Ticket -->
            <div class="mb-6">
              <!-- Fecha y Categoría - Stack en móvil -->
              <div class="ticket-meta-section mb-3">
                <CambiarCategoriaTicketComponent v-if="ticket.tickets_anidados.length == 0" class="ticket-category" />
                <div class="ticket-date text-caption text-medium-emphasis">
                  {{ fechasmall("DD [de] MMMM, YYYY", ticket.created_at) }} • {{ fechasmall("hh:mm a", ticket.created_at) }}
                </div>
              </div>
              
              <!-- Título del Ticket - Responsive -->
              <h1 class="ticket-title font-weight-bold text-grey-darken-4 mb-3">
                {{ ticket.tickets_anidados.length == 0 ? ticket.asunto : 'Continuación: ' + ticket.asunto }}
              </h1>

              <!-- Info Solicitante (Compacto) -->
              <div class="ticket-author-inline d-flex align-center mb-4">
                <v-avatar :size="$vuetify.display.smAndDown ? 24 : 28" class="ticket-avatar-small mr-2 flex-shrink-0">
                  <img v-if="ticket.solicitante.avatar" :src="ticket.solicitante.avatar" alt="Avatar" style="object-fit: cover; width: 100%; height: 100%;">
                  <v-img v-else :src="require('@/assets/' + ticket.solicitante.tipouser.toLowerCase() + '.png')" cover></v-img>
                </v-avatar>
                <div class="ticket-author-info">
                  <span class="ticket-author-name-inline">
                    {{ capitalizeWords(ticket.solicitante.nombres + ' ' + ticket.solicitante.apellidos) }}
                  </span>
                  <span class="ticket-author-separator">·</span>
                  <span class="ticket-author-role-inline">
                    {{ capitalizeWords(ticket.solicitante.tipouser === 'colaborador' ? ticket.solicitante.cargo : ticket.solicitante.carrera) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Cuerpo del Ticket -->
            <div class="body-ticket caja-texto-styles text-body-1 text-grey-darken-3 mb-8" v-html="ticket.body" 
                 style="font-family: 'Plus Jakarta Sans', sans-serif; line-height: 1.8;"></div>

            <!-- Archivos Adjuntos -->
            <div v-if="ticket.fileadjunto && ticket.fileadjunto.length > 0" class="mt-6 pt-6 border-t">
              <div class="text-subtitle-2 font-weight-bold mb-3 d-flex align-center text-grey-darken-2">
                <v-icon style="font-size: 14px; color: #757575;" class="mr-2">fa:fa-solid fa-paperclip</v-icon>
                Archivos Adjuntos
              </div>
              <div class="d-flex flex-wrap gap-2">
                <template v-if="!(ticket.fileadjunto instanceof Array)">
                   <v-chip label variant="outlined" color="grey-darken-1" class="bg-white" @click="onOpenDialogAdjunto(ticket.fileadjunto)">
                      <v-icon style="font-size: 14px; color: #546E7A;" class="mr-2">fa:fa-solid fa-file</v-icon>
                      {{ getFileNameFromURL(ticket.fileadjunto) ? getFileNameFromURL(ticket.fileadjunto)['name'] : 'Archivo' }}
                   </v-chip>
                </template>
                <template v-else>
                  <v-chip v-for="file in ticket.fileadjunto" :key="file" label variant="outlined" color="grey-darken-1" class="bg-white pr-4" @click="onOpenDialogAdjunto(file)">
                    <v-avatar start size="24" class="mr-2">
                      <v-img :src="require('@/assets/' + iconExtension(getFileNameFromURL(file)['extension']))" cover></v-img>
                    </v-avatar>
                    <span class="text-truncate" style="max-width: 180px;">
                      {{ getFileNameFromURL(file) ? getFileNameFromURL(file)['name'] : '' }}
                    </span>
                  </v-chip>
                </template>
              </div>
            </div>
          </div>

          <!-- Comentarios y Respuestas -->
          <div class="mt-6">
            <!-- Componente de Respuesta Inline -->

            <div v-if="!isDisabled(ticket.estado)" id="answer-form-anchor" ref="answerFormRef">
               <CommentComponent :idticket="ticket.idticket" />
            </div>


            <div class="mt-8">
              <div class="d-flex align-center mb-4">
                <v-divider></v-divider>
                <span class="text-caption text-medium-emphasis px-4 text-uppercase font-weight-bold">Respuestas Anteriores</span>
                <v-divider></v-divider>
              </div>
              <AnswersFromTicketComponent :idticket="ticket.idticket" />   
            </div>
          </div>

        </v-col>

        <!-- COLUMNA LATERAL: METADATOS Y ACCIONES -->
        <v-col cols="12" md="4" lg="3">
          <div class="sticky-sidebar">
            
            <!-- Panel de Acciones/Estado -->
            <div class="bg-white rounded-lg border pa-4 mb-4 shadow-sm">
              <div class="text-caption font-weight-bold text-medium-emphasis mb-3 text-uppercase ls-1">Estado & Acciones</div>
              
              <ActualizarTicketComponent :readonly="isRoleVisitor()" :class="disableClass(ticket.estado)" class="mb-3 w-100" />
              
              <ConfirmResultTicketComponent class="mb-3 w-100" v-if="ticket.estado && ticket.estado.isCierre" :ticket="ticket" />
              
              <CrearTicketAnidadoComponent :ticket="ticket" class="w-100"></CrearTicketAnidadoComponent>
            </div>

            <!-- Panel de Información -->
            <div class="bg-white rounded-lg border pa-4 mb-4 shadow-sm">
              <div class="text-caption font-weight-bold text-medium-emphasis mb-3 text-uppercase ls-1">Detalles</div>
              <InfoTicketComponent :ticket="ticket" />
            </div>

            <!-- Panel de Información Adicional -->
            <div class="bg-white rounded-lg border pa-4 mb-4 shadow-sm" v-if="ticket.destino || ticket.formato || ticket.link_referencia">
              <DetallesAdicionalTicketComponent :ticket="ticket" />
            </div>

            <!-- Panel de CC -->
            <div class="bg-white rounded-lg border pa-4 shadow-sm">
              <div class="text-caption font-weight-bold text-medium-emphasis mb-3 text-uppercase ls-1">Participantes</div>
              <CCTicketComponent />
            </div>

          </div>
        </v-col>

      </v-row>

      <VisualizarAdjuntoComponent></VisualizarAdjuntoComponent>
    </v-container>
  </div>
</template>

<script>

import InfoTicketComponent from "../components/VisualizarTicket/InfoTicketComponent.vue";
import DetallesAdicionalTicketComponent from "../components/VisualizarTicket/DetallesAdicionalTicketComponent.vue";
import CommentComponent from "../components/VisualizarTicket/CommentComponent.vue";
import NotasComponent from "../components/VisualizarTicket/NotasComponent.vue";
//import ComentariosComponent from "../components/VisualizarTicket/ComentariosComponent.vue";
import ActualizarTicketComponent from "../components/VisualizarTicket/ActualizarTicketComponent.vue";
import ContadorAtencionComponent from "../components/VisualizarTicket/ContadorAtencionComponent.vue"
import ChatInitView from '@/modules/Chat/views/ChatInitView.vue'
import CambiarCategoriaTicketComponent from "../components/CambiarCategoriaTicketComponent.vue";
import ConfirmResultTicketComponent from "../components/VisualizarTicket/ConfirmResultTicketComponent.vue";
import CrearTicketAnidadoComponent from "../components/VisualizarTicket/CrearTicketAnidadoComponent.vue";

import AnswersFromTicketComponent from "@/modules/Answers/components/AnswersFromTicketComponent.vue"

import { mapState, mapActions, mapMutations } from "vuex";
import Filters from "@/modules/Shared/Filters";
import { socket } from "@/ws-client";
import VisualizarAdjuntoComponent from "../components/VisualizarTicket/VisualizarAdjuntoComponent.vue";

import CCTicketComponent from "../components/VisualizarTicket/CCTicketComponent.vue";

export default {
  mixins: [Filters],
  components: {
    InfoTicketComponent,
    DetallesAdicionalTicketComponent,
    CommentComponent,
    NotasComponent,
    AnswersFromTicketComponent,
    ActualizarTicketComponent,
    ContadorAtencionComponent,
    ChatInitView,
    CambiarCategoriaTicketComponent,
    ConfirmResultTicketComponent,
    CrearTicketAnidadoComponent,
    VisualizarAdjuntoComponent,
    CCTicketComponent
  },
  data() {
    return {
      comentar: false,
    };
  },
  watch: {
    listTickets:
    {
      handler(newval, oldval) {
        if (newval) {
          this.getTicket(this.$route.params.codigoticket)
        }
      }
    },
    ticket: {
      handler(newval) {
        if (this.ticket) {
          this.openTicket(this.ticket.idticket)
        }
      }
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapState('tickets', ['ticket', 'listTickets', 'dialogFileAdjunto']),

    cierreBTN() {
      if (this.ticket) {
        if (this.ticket.estado == null) {
          return true
        }
        else if (this.ticket.estado.isCierre == 0) {
          return true
        }
        else {
          return false
        }
      }
    },


  },

  methods:
  {
    ...mapActions('tickets', ['getTicket', 'closedTicket', 'deleteTicket', 'openTicket']),
    ...mapMutations('tickets', ['resetTicket', 'switchDialogFileAdjunto', 'setUrlFile', 'resetUrlFile']),
    ...mapMutations('answers', ['switchDialogTicket', 'resetListAnswers']),

    obtenerTicketsRelacionados(ticket, arrayTickets = []) {
      // Verificar si el ticket tiene tickets relacionados
      if (ticket.tickets_relacionados && typeof ticket.tickets_relacionados === 'object') {
        arrayTickets.push(ticket.tickets_relacionados)
        this.obtenerTicketsRelacionados(ticket, arrayTickets)
      }
    },

    onOpenDialogAdjunto(file) {
      this.resetUrlFile()
      this.switchDialogFileAdjunto()
      this.setUrlFile(file)
    },

    scrollToAnswer() {
      const el = document.getElementById('answer-form-anchor');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

  },

  mounted() {

    let codigoticket = null;
    codigoticket = this.$route.params.codigoticket;

    this.resetUrlFile()
    this.resetListAnswers()
    this.resetTicket()

    socket.on('UPDATE_PREVIEW_TICKET', data => {
      this.resetTicket()
      this.getTicket(codigoticket)
    })

    this.getTicket(codigoticket) 

    if (this.isRoleVisitor()) {
      this.$router.push({ name: 'visualizeTicket' })
    }

  }
};
</script>

<style>
.body-ticket img {
  max-width: 100% !important;
  border-radius: 8px;
}

.caja-texto-styles p {
  margin-bottom: 12px;
}

.min-h-screen {
  min-height: 100vh;
}

.sticky-top {
  position: sticky;
  top: 64px;
  z-index: 10;
}

.sticky-sidebar {
  position: sticky;
  top: 140px;
}

.ls-1 {
  letter-spacing: 1px;
}

.shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.gap-2 {
  gap: 8px;
}

/* Estilos para Barra de Herramientas Responsive */
.toolbar-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.toolbar-container {
  padding: 0.75rem 1.5rem;
  min-height: 56px;
}

.toolbar-actions {
  gap: 0.5rem;
}

.toolbar-btn {
  text-transform: none;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 0.5rem 1rem;
}

.toolbar-icon {
  font-size: 14px;
  margin-right: 0.5rem;
}

.toolbar-divider {
  margin: 0.25rem 0.5rem;
}

.btn-text {
  display: inline;
}

/* Tablet y Móvil */
@media (max-width: 960px) {
  .toolbar-container {
    padding: 0.65rem 1rem;
    min-height: 52px;
  }
  
  .toolbar-btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.7rem;
  }
  
  .toolbar-icon {
    font-size: 13px;
    margin-right: 0.4rem;
  }
  
  .toolbar-divider {
    margin: 0.25rem 0.4rem;
  }
}

/* Móvil Pequeño - Solo Iconos */
@media (max-width: 600px) {
  .toolbar-container {
    padding: 0.6rem 0.75rem;
    min-height: 48px;
  }
  
  .toolbar-actions {
    gap: 0.25rem;
  }
  
  .toolbar-btn {
    min-width: auto !important;
    padding: 0.5rem !important;
    width: auto;
  }
  
  .toolbar-icon {
    font-size: 16px;
    margin-right: 0 !important;
  }
  
  .btn-text {
    display: none;
  }
  
  .toolbar-divider {
    margin: 0.25rem 0.3rem;
  }
}

/* Móvil Extra Pequeño */
@media (max-width: 400px) {
  .toolbar-container {
    padding: 0.55rem 0.5rem;
    min-height: 44px;
  }
  
  .toolbar-actions {
    gap: 0.15rem;
  }
  
  .toolbar-btn {
    padding: 0.4rem !important;
  }
  
  .toolbar-icon {
    font-size: 15px;
  }
  
  .toolbar-divider {
    margin: 0.25rem 0.2rem;
  }
}

/* Estilos para el Header del Ticket - Desktop */
.ticket-header-card {
  padding: 2rem;
}

.ticket-meta-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.ticket-category {
  flex-shrink: 0;
}

.ticket-date {
  flex-shrink: 0;
  white-space: nowrap;
}

.ticket-title {
  font-size: 1.5rem;
  line-height: 1.3;
}

/* Info del autor - Diseño inline compacto */
.ticket-author-inline {
  padding: 0;
}

.ticket-avatar-small {
  border: 1.5px solid #E0E0E0;
}

.ticket-author-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  line-height: 1.4;
}

.ticket-author-name-inline {
  font-size: 13px;
  font-weight: 600;
  color: #424242;
}

.ticket-author-separator {
  font-size: 12px;
  color: #BDBDBD;
  font-weight: 400;
  margin: 0 2px;
}

.ticket-author-role-inline {
  font-size: 12px;
  font-weight: 400;
  color: #757575;
}

/* Tablet */
@media (max-width: 960px) {
  .ticket-header-card {
    padding: 1.5rem;
  }
  
  .ticket-title {
    font-size: 1.3rem;
  }
}

/* Móvil */
@media (max-width: 600px) {
  .ticket-header-card {
    padding: 1rem;
  }
  
  .ticket-meta-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
  
  .ticket-category {
    order: 2;
    width: 100%;
  }
  
  .ticket-date {
    order: 1;
    font-size: 0.7rem;
  }
  
  .ticket-title {
    font-size: 1.1rem;
    line-height: 1.4;
    word-break: break-word;
  }
  
  .ticket-author-name-inline {
    font-size: 12px;
  }
  
  .ticket-author-separator {
    font-size: 11px;
  }
  
  .ticket-author-role-inline {
    font-size: 11px;
  }
}

/* Móvil Pequeño */
@media (max-width: 400px) {
  .ticket-header-card {
    padding: 0.85rem;
  }
  
  .ticket-title {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }
  
  .ticket-author-name-inline {
    font-size: 11px;
  }
  
  .ticket-author-separator {
    font-size: 10px;
    margin: 0 1px;
  }
  
  .ticket-author-role-inline {
    font-size: 10px;
  }
}

/* Utilidades */
.min-width-0 {
  min-width: 0;
}
</style>